import { Params } from '@angular/router';

import { UrlParser } from './url-parser.utility';

export class UrlBuilder {
    public static appendQueryParam(url: string, name: string, value: string) {
        if (!url || !name) {
            return url;
        }

        const params: Params = {};
        params[name] = value;
        return UrlBuilder.appendQueryParams(url, params);
    }

    public static appendQueryParams(url: string, params: Params, disableEncodedOutput?: boolean): string {
        if (!url) {
            return url;
        }

        return UrlBuilder.appendQuery(url, UrlBuilder.buildQueryPath(params, disableEncodedOutput));
    }

    public static appendQuery(url: string, query: string): string {
        if (!url || !query) {
            return url;
        }
        const { protocol, hostname, port, path, search, hash } = UrlParser.getLocation(url);
        return `${protocol}//${hostname}${port ? ':' + port : ''}${path}${search ? search + '&' : '?'}${query}${hash}`;
    }
    /**
     * @param  {string} url -full url containing protocol, hostname, path, and hash
     * @param  {Params} params -params to add or replace with existing param in the url
     * @param  {boolean} isEncoded -if url has params that are encoded, decode them, and compare them with the param keys that match and they will be replaced.
     * @param  {boolean} disableEncodedOutput -disables encoding of the value of each param in the final output
     * @returns string
     */
    public static appendAndReplaceQueryParams(url: string, params: Params, isEncoded: boolean, disableEncodedOutput?: boolean): string {
        if (!url || !params) {
            return url;
        }
        const newParams: Params = {};
        const { protocol, hostname, path, search, hash } = UrlParser.getLocation(url);
        if (search[0] === '?') {
            const queryList = search.substring(1).split('&');
            queryList.forEach( q => {
                const s = q.split('=');
                if (!isEncoded) {
                    newParams[s[0]] = s[1];
                } else {
                    newParams[decodeURIComponent(s[0])] = decodeURIComponent(s[1]);
                }
            });
        }

        for (const field in params) {
            newParams[field] = params[field];
        }
        return UrlBuilder.appendQueryParams(protocol + '//' + hostname + path + hash, newParams, disableEncodedOutput);
    }

    public static appendLoginHint(url: string, loginHint: string, loginHintEnabled: boolean): string {
        if (loginHintEnabled) {
            return UrlBuilder.appendQueryParam(url, 'login_hint', loginHint);
        }

        return url;
    }

    /**
     * @param  {string} url -full url containing protocol, hostname, path, and hash
     * @param  {Params} params -params to add or replace with existing param in the url
     * @param  {boolean} isEncoded -if url has params that are encoded, decode them, and compare them with the param keys that match and they will be replaced.
     * @param  {boolean} disableEncodedOutput -disables encoding of the value of each param in the final output
     * @returns string
     */
    public static removeQueryParams(url: string, params: Array<string>, isEncoded: boolean): string {
        if (!url || !params) {
            return url;
        }
        const newParams: Params = {};
        const { protocol, hostname, path, search, hash } = UrlParser.getLocation(url);
        if (search[0] === '?') {
            const queryList = search.substring(1).split('&');
            queryList.forEach( q => {
                const s = q.split('=');
                const key = isEncoded ? decodeURIComponent(s[0]) : s[0];
                if (!params.some(x => x.toLowerCase() === key.toLowerCase())) {
                    newParams[key] = isEncoded ? decodeURIComponent(s[1]) : s[1];
                }
            });
        }

        return UrlBuilder.appendQueryParams(protocol + '//' + hostname + path + hash, newParams, false);
    }

    public static buildQueryPath(params: Params, disableEncodedOutput?: boolean): string {
        if (!params) {
            return '';
        }

        let query = '';
        for (const key in params) {
            const querySeparator = query ? '&' : '';
            let value;
            if (disableEncodedOutput) {
                value = params[key] ? `${params[key]}` : '';
            } else {
                value = params[key] ? `${encodeURIComponent(params[key])}` : '';
            }
            query = `${query}${querySeparator}${key}=${value}`;
        }

        return query;
    }

    public static replacePathAndAddParams(url: string, newPath: string, params?: Params): string {
        const withNewParams = params ? UrlBuilder.appendAndReplaceQueryParams(url, params, false) : url;
        const { protocol, hostname, port, search, hash } = UrlParser.getLocation(withNewParams);
        return `${protocol}//${hostname}${port ? ':' + port : ''}${newPath}${search}${hash}`;
    }
}
